"use client";

import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const photoCards = [{
  label: "HOME REPAIRS",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/home_repair_service.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/home_repair_service.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/home_repair_service.png-wvc-sizes",
  alt: "Local repair pro working on a modern kitchen appliance with precision tools",
  gridClass: "md:col-span-4 md:row-start-1"
}, {
  label: "HVAC SOLUTIONS",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_maintenance_service.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_maintenance_service.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_maintenance_service.webp-wvc-sizes",
  alt: "HVAC pro inspecting a residential climate control unit for optimal efficiency",
  gridClass: "md:col-span-4 md:row-start-1"
}, {
  label: "ELECTRICAL WORK",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/electrical_safety_service.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/electrical_safety_service.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/electrical_safety_service.png-wvc-sizes",
  alt: "Independent electrician working on a home electrical panel with safety gear",
  gridClass: "md:col-span-4 md:row-start-2"
}];
export default function Services() {
  const container = useRef<HTMLElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add({
      reduced: "(prefers-reduced-motion: reduce)",
      normal: "(prefers-reduced-motion: no-preference)"
    }, ctx => {
      const {
        reduced
      } = ctx.conditions as {
        reduced: boolean;
      };
      if (reduced) {
        gsap.set([".bespoke-eyebrow", ".bespoke-heading", ".bespoke-cell"], {
          opacity: 1,
          y: 0
        });
        return;
      }
      const headerTl = gsap.timeline({
        scrollTrigger: {
          trigger: container.current,
          start: "top 82%"
        },
        defaults: {
          ease: "cubic-bezier(0.22, 0.61, 0.36, 1)"
        }
      });
      headerTl.from(".bespoke-eyebrow", {
        opacity: 0,
        y: 12,
        duration: 0.5
      }).from(".bespoke-heading", {
        opacity: 0,
        y: 20,
        duration: 0.7
      }, "-=0.38");
      gsap.from(".bespoke-cell", {
        opacity: 0,
        y: 12,
        duration: 0.6,
        stagger: 0.06,
        ease: "cubic-bezier(0.22, 0.61, 0.36, 1)",
        scrollTrigger: {
          trigger: ".bespoke-grid",
          start: "top 85%"
        }
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="24617" id="servicesgrid" ref={container} data-nav="light" className="bg-background py-28 md:py-40 overflow-hidden">
      <div className="mx-auto max-w-[1360px] px-5 md:px-10">
        <div className="max-w-4xl">
          <div className="bespoke-eyebrow flex items-center gap-3 mb-6">
            <span aria-hidden="true" className="inline-block w-[1.5px] h-3 bg-[var(--primary)]" />
            <span className="font-default text-xs md:text-sm font-semibold uppercase tracking-[0.22em]" style={{
            color: "var(--primary)"
          }}>
              Our Services
            </span>
            <span aria-hidden="true" className="inline-block w-[1.5px] h-3 bg-[var(--primary)]" />
          </div>

          <h2 className="bespoke-heading font-serif uppercase font-bold text-foreground" style={{
          fontSize: "clamp(2.5rem, 6vw, 5.25rem)",
          letterSpacing: "-0.005em",
          lineHeight: 0.94,
          maxWidth: "20ch"
        }}>
            Expert home care, just a click away.
          </h2>
        </div>

        <div className="bespoke-grid mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 md:grid-rows-2 gap-6">
          <div data-index={0} className="bespoke-cell group relative md:col-span-4 md:row-span-2 border border-border bg-card p-8 md:p-10 overflow-hidden transition-colors duration-300 hover:border-[var(--primary)]" style={{
          borderRadius: "var(--radius-lg)"
        }}>
            <svg aria-hidden="true" viewBox="0 0 200 80" className="absolute bottom-4 right-4 w-40 h-16 pointer-events-none" style={{
            opacity: 0.06
          }} fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="square" strokeLinejoin="miter">
              <path d="M0 70 L40 40 L70 55 L100 20 L115 35 L110 55 L120 55 L120 45 L130 45 L130 55 L145 55 L130 30 L160 50 L200 25" />
            </svg>

            <div className="relative flex flex-col h-full">
              <h3 className="font-serif uppercase text-foreground font-semibold" style={{
              fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)",
              lineHeight: 1.05
            }}>
                Trusted local pros.
              </h3>

              <p className="font-default text-base md:text-[1.0625rem] text-foreground/80 leading-relaxed mt-8">
                Connect with verified contractors for appliance repair, HVAC, 
                plumbing, and electrical needs. We ensure quality, safety, 
                and reliability for every home project you book.
              </p>

              <div className="mt-10 flex-1 flex items-end">
                <Button asChild className="rounded-lg px-8 py-5 h-auto font-default text-xs uppercase tracking-[0.04em] font-semibold bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--primary)]/90">
                  <Link to="#bookingrequest">Find a Pro</Link>
                </Button>
              </div>
            </div>
          </div>

          {photoCards.map((card, i) => <div data-index={i + 1} key={card.label} className={`bespoke-cell group relative overflow-hidden ${card.gridClass}`} style={{
          borderRadius: "var(--radius-lg)"
        }}>
              <div className="relative w-full aspect-[4/3] md:aspect-auto md:h-full overflow-hidden">
                <img src={card.src} data-wvc-srcset={card.srcset} data-wvc-sizes={card.sizes} alt={card.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />
                <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{
              background: "linear-gradient(to bottom, hsla(220, 12%, 11%, 0.3) 0%, hsla(220, 12%, 11%, 0) 45%, hsla(220, 12%, 11%, 0.35) 100%)"
            }} />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="inline-block font-default text-xs md:text-sm font-semibold uppercase tracking-[0.22em] text-[var(--background)] relative pb-1">
                    {card.label}
                    <span aria-hidden="true" className="absolute left-0 bottom-0 h-px bg-[var(--background)] w-0 group-hover:w-full transition-all duration-[320ms] ease-out" />
                  </span>
                </div>
              </div>
            </div>)}

          <div data-index={4} className="bespoke-cell group relative md:col-span-4 md:row-start-2 p-8 md:p-10 overflow-hidden bg-[var(--primary)]" style={{
          borderRadius: "var(--radius-lg)"
        }}>
            <div className="flex flex-col h-full">
              <h3 className="font-serif uppercase font-semibold text-[var(--primary-foreground)]" style={{
              fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)",
              lineHeight: 1.05
            }}>
                Join our network.
              </h3>

              <p className="font-default text-base text-[var(--primary-foreground)]/85 leading-relaxed mt-6 max-w-sm">
                Grow your business with our lead distribution and scheduling 
                tools. Register today to start receiving local service requests.
              </p>

              <div className="mt-10 flex-1 flex items-end">
                <Link to="/JoinTheNetworkPage" className="inline-flex items-center gap-3 font-default text-xs uppercase tracking-[0.04em] font-semibold text-[var(--primary-foreground)] group/cta">
                  <span className="relative pb-1">
                    Register now
                    <span aria-hidden="true" className="absolute left-0 bottom-0 h-px bg-[var(--primary-foreground)] w-full origin-left" />
                  </span>
                  <ArrowRight className="w-4 h-4 transition-transform duration-[240ms] ease-out group-hover/cta:translate-x-1.5" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}